import * as Sentry from "@sentry/react";
import { useLocalStorage } from "./useLocalStorage";
import { userStorageKey } from "./userStorageKey";

const SELECTED_SITES_KEY = "selectedSites";

const selectedSitesKey = (userId: string): string =>
  userStorageKey({ userId, key: SELECTED_SITES_KEY });

// In-memory cache to avoid repeated localStorage reads on every request
// NOTE: This will not propagate across multiple tabs, which _could_ lead to some minor confusion
let cachedSites: { userId: string; sites: string[] | null } | null = null;

const parseStoredSites = (storedValue: string | null): string[] | null => {
  if (!storedValue) {
    return null;
  }

  try {
    const parsed = JSON.parse(storedValue);
    if (!Array.isArray(parsed)) {
      Sentry.captureException(
        new Error(`Expected array in selectedSites, got ${typeof parsed}`),
      );
      return null;
    }
    return parsed;
  } catch (error) {
    Sentry.captureException(error);
    return null;
  }
};

/**
 * Retrieves the selected sites from localStorage (with in-memory caching).
 *
 * The first call reads from localStorage, subsequent calls return the cached value
 * until setSelectedSites is called or a different userId is requested.
 *
 * @param userId - User ID to scope the storage key
 * @returns Array of site identifiers, or null if none selected
 *
 * @example
 * ```ts
 * const sites = getSelectedSites("user-123");
 * // Returns: ["North", "South"]
 * ```
 */
export const getSelectedSites = (userId: string): string[] | null => {
  if (cachedSites?.userId === userId) {
    return cachedSites.sites;
  }

  const storedSites = localStorage.getItem(selectedSitesKey(userId));
  const sites = parseStoredSites(storedSites);
  cachedSites = { userId, sites };
  return sites;
};

/**
 * Stores the selected sites in localStorage and updates the in-memory cache.
 *
 * @param sites - Array of site identifiers to store
 * @param userId - User ID to scope the storage key
 *
 * @example
 * ```ts
 * setSelectedSites(["North", "South"], "user-123");
 * ```
 */
export const setSelectedSites = (sites: string[], userId: string): void => {
  try {
    localStorage.setItem(selectedSitesKey(userId), JSON.stringify(sites));
    // Update cache immediately
    cachedSites = { userId, sites };
    // Notify React components of the change
    window.dispatchEvent(new Event("storage"));
  } catch (error) {
    // QuotaExceededError or SecurityError (private browsing mode)
    Sentry.captureException(error);
  }
};

/**
 * Injects selected site(s) into the given request headers.
 *
 * @param headers - The request Headers to modify
 * @param options - Either userId to fetch from storage, or explicit sites array
 *
 * @example
 * ```ts
 * // Fetch from localStorage using userId
 * injectSelectedSites(request.headers, { userId: "user-123" });
 * // Result: Medoma-Site: North, South (from storage)
 *
 * // Explicit sites
 * injectSelectedSites(request.headers, { sites: ["North", "South"] });
 * // Result: Medoma-Site: North, South
 * ```
 */
export const injectSelectedSites = (
  headers: Headers,
  options:
    | { userId: string; sites?: never }
    | { sites: string[]; userId?: never },
): void => {
  const sitesToInject = options.sites ?? getSelectedSites(options.userId);
  if (sitesToInject) {
    headers.set("Medoma-Site", sitesToInject.join(", "));
  }
};

/**
 * React hook to get selected sites with automatic reactivity.
 *
 * Uses useSyncExternalStore under the hood to subscribe to localStorage changes.
 * When setSelectedSites is called, all components using this hook will re-render.
 *
 * @param userId - User ID to scope the storage key
 * @returns Array of site identifiers, or null if none selected
 *
 * @example
 * ```tsx
 * const MyComponent = ({ userId }: { userId: string }) => {
 *   const selectedSites = useSelectedSites(userId);
 *
 *   if (!selectedSites) {
 *     return <SiteSelector userId={userId} />;
 *   }
 *
 *   return <div>Selected sites: {selectedSites.join(", ")}</div>;
 * };
 * ```
 */
export const useSelectedSites = (userId: string): string[] | null => {
  const [storedValue] = useLocalStorage({ key: SELECTED_SITES_KEY, userId });
  return parseStoredSites(storedValue || null);
};

export const _resetCacheForTesting = (): void => {
  cachedSites = null;
};
